import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Separator } from './ui/separator';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from './ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from './ui/tooltip';
import {
  FileText,
  Star,
  Search,
  Settings,
  X,
  Download,
  CheckCircle,
  AlertTriangle,
  Loader2
} from 'lucide-react';
import templateService from '../services/templateService';

const TemplateSelector = ({
  open,
  onClose,
  onSelect,
  entityType,
  language = 'cn',
  title = 'Select Template',
  allowManagement = false
}) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState(language || 'all');
  const [viewMode, setViewMode] = useState('grid');

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const options = {
        entityType,
        language: selectedLanguage === 'all' ? undefined : selectedLanguage,
        search: searchTerm || undefined,
        pageSize: 50
      };

      const response = await templateService.listTemplates(options);
      setTemplates(response.templates || []);

      // Auto-select default template if available and no current selection
      if (!selectedTemplate && response.templates.length > 0) {
        const defaultTemplate = response.templates.find(t => t.is_default);
        if (defaultTemplate) {
          setSelectedTemplate(defaultTemplate);
        }
      }
    } catch (err) {
      console.error('Error loading templates:', err);
      setError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [entityType, selectedLanguage, searchTerm, selectedTemplate]);

  useEffect(() => {
    if (open) {
      loadTemplates();
    }
  }, [open, loadTemplates]);

  const handleSelect = () => {
    if (selectedTemplate && onSelect) {
      onSelect(selectedTemplate);
    }
    onClose();
  };

  const handleClose = () => {
    setSelectedTemplate(null);
    setSearchTerm('');
    onClose();
  };

  const handleTemplateClick = (template) => {
    setSelectedTemplate(template);
  };

  const handleDownloadPreview = async (template, event) => {
    event.stopPropagation();
    try {
      const { blob, filename } = await templateService.downloadTemplate(template.template_id);
      templateService.downloadBlob(blob, filename);
    } catch (err) {
      console.error('Error downloading template:', err);
      setError('Failed to download template preview');
    }
  };

  const getMetadataStatus = (template) => {
    if (template.has_metadata && template.metadata) {
      const headerMappings = template.metadata.header_mappings || [];
      const tableColumns = template.metadata.table_config?.columns || [];
      const mappedFields = new Set(tableColumns.map(c => c.field));
      const required = ['name', 'quantity'];
      const missingRequired = required.filter(r => !mappedFields.has(r) && !mappedFields.has(`__computed_${r}`));

      if (missingRequired.length === 0 && headerMappings.length > 0 && tableColumns.length > 0) {
        return {
          status: 'complete',
          label: 'Mappings Defined',
          variant: 'default',
          Icon: CheckCircle,
          tooltip: `${headerMappings.length} headers, ${tableColumns.length} columns mapped`
        };
      } else if (headerMappings.length > 0 || tableColumns.length > 0) {
        return {
          status: 'partial',
          label: 'Partial Mapping',
          variant: 'outline',
          Icon: AlertTriangle,
          tooltip: missingRequired.length > 0
            ? `Missing required: ${missingRequired.join(', ')}`
            : 'Some mappings defined, may need completion'
        };
      }
    }

    return {
      status: 'none',
      label: 'Needs Mapping',
      variant: 'outline',
      Icon: AlertTriangle,
      tooltip: 'Template needs field mappings to work properly'
    };
  };

  const GridView = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
      {templates.map((template) => {
        const metadataStatus = getMetadataStatus(template);
        const StatusIcon = metadataStatus.Icon;

        return (
          <Card
            key={template._id}
            className={`cursor-pointer transition-all hover:shadow-md ${
              selectedTemplate?._id === template._id
                ? 'ring-2 ring-blue-500'
                : ''
            }`}
            onClick={() => handleTemplateClick(template)}
          >
            <CardContent className="p-4">
              <div className="flex items-start mb-2">
                <FileText className="h-5 w-5 mr-2 mt-0.5 text-primary" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1">
                    <p className="text-sm font-medium truncate">
                      {template.name}
                    </p>
                    {template.is_default && (
                      <Star className="h-4 w-4 text-warning fill-yellow-500" />
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">
                    {template.description || 'No description'}
                  </p>
                </div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={(e) => handleDownloadPreview(template, e)}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Download Preview</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>

              <div className="flex gap-1 flex-wrap mt-2">
                <Badge variant="outline" className="text-xs">
                  {templateService.getLanguageDisplayName(template.language)}
                </Badge>
                {template.is_default && (
                  <Badge className="text-xs bg-warning/20 text-warning hover:bg-warning/20">
                    默认
                  </Badge>
                )}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge variant={metadataStatus.variant} className="text-xs gap-1">
                        <StatusIcon className="h-3 w-3" />
                        {metadataStatus.label}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>{metadataStatus.tooltip}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );

  const ListView = () => (
    <div className="divide-y">
      {templates.map((template) => {
        const metadataStatus = getMetadataStatus(template);
        const StatusIcon = metadataStatus.Icon;

        return (
          <div
            key={template._id}
            className={`flex items-center p-3 cursor-pointer hover:bg-muted ${
              selectedTemplate?._id === template._id ? 'bg-primary/10' : ''
            }`}
            onClick={() => handleTemplateClick(template)}
          >
            <input
              type="radio"
              checked={selectedTemplate?._id === template._id}
              onChange={() => handleTemplateClick(template)}
              className="mr-3"
            />

            <FileText className="h-5 w-5 mr-3 text-primary" />

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium">{template.name}</span>
                {template.is_default && (
                  <Star className="h-4 w-4 text-warning fill-yellow-500" />
                )}
                <Badge variant="outline" className="text-xs">
                  {templateService.getLanguageDisplayName(template.language)}
                </Badge>
                {template.is_default && (
                  <Badge className="text-xs bg-warning/20 text-warning hover:bg-warning/20">
                    默认
                  </Badge>
                )}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge variant={metadataStatus.variant} className="text-xs gap-1">
                        <StatusIcon className="h-3 w-3" />
                        {metadataStatus.label}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>{metadataStatus.tooltip}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <p className="text-sm text-muted-foreground truncate">
                {template.description || 'No description'}
              </p>
            </div>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={(e) => handleDownloadPreview(template, e)}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Download Preview</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        );
      })}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>{title}</DialogTitle>
            <Button variant="ghost" size="icon" onClick={handleClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Search and Filters */}
          <div className="flex gap-3 mb-4 flex-wrap">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search templates"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>

            <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="Language" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="cn">中文</SelectItem>
                <SelectItem value="en">English</SelectItem>
              </SelectContent>
            </Select>

            <Select value={viewMode} onValueChange={setViewMode}>
              <SelectTrigger className="w-[100px]">
                <SelectValue placeholder="View" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="grid">Grid</SelectItem>
                <SelectItem value="list">List</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Error Display */}
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription className="flex items-center justify-between">
                {error}
                <Button variant="ghost" size="sm" onClick={() => setError(null)}>
                  <X className="h-4 w-4" />
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {/* Templates Display */}
          <div className="flex-1 overflow-auto min-h-[400px]">
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : templates.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">
                  No templates found for this entity type.
                </p>
                {allowManagement && (
                  <Button
                    className="mt-4"
                    variant="outline"
                    onClick={() => {
                      console.log('Open template management');
                    }}
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Manage Templates
                  </Button>
                )}
              </div>
            ) : (
              <>
                {viewMode === 'grid' ? <GridView /> : <ListView />}

                {/* Selection Summary */}
                {selectedTemplate && (
                  <div className="mt-4 p-3 bg-muted rounded-md">
                    <Label className="text-sm font-medium">Selected Template:</Label>
                    <p className="text-sm mt-1">
                      <strong>{selectedTemplate.name}</strong>
                      {selectedTemplate.description && (
                        <span className="text-muted-foreground">
                          {' - '}{selectedTemplate.description}
                        </span>
                      )}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <Separator className="my-4" />

        <DialogFooter>
          {allowManagement && (
            <Button
              variant="outline"
              onClick={() => {
                console.log('Open template management');
              }}
              className="mr-auto"
            >
              <Settings className="h-4 w-4 mr-2" />
              Manage Templates
            </Button>
          )}

          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>

          <Button
            onClick={handleSelect}
            disabled={!selectedTemplate}
          >
            Select Template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TemplateSelector;
